import React from "react";
import Input from "@/components/common/Form/Input";
import { Table, Td } from "@/components/Table";
import { Icon } from "@/components/Icons";
import { ProductSearchItem } from "../types";

interface ProductLookupProps {
  value: string;
  productResults: ProductSearchItem[];
  selectedProductIndex: number;
  dropdownPosition: {
    top: number;
    left: number;
    width: number;
    openUpward: boolean;
  } | null;
  isLoadingProducts?: boolean;
  disabled?: boolean;
  inputRef: React.RefObject<HTMLDivElement>;
  selectedProductRowRef: React.RefObject<HTMLTableRowElement | null>;
  justClosedDropdownRef: React.MutableRefObject<boolean>;
  onChange: (value: string) => void;
  onFocus: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onProductPick: (product: ProductSearchItem) => void;
  onSetActiveRow: (active: boolean) => void;
  onSetProductResults: (results: ProductSearchItem[]) => void;
  onSetDropdownPosition: (
    position: {
      top: number;
      left: number;
      width: number;
      openUpward: boolean;
    } | null
  ) => void;
  onSetSelectedIndex: (index: number) => void;
}

const formatCurrency = (value?: number) =>
  Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
  }).format(value || 0);

const ProductLookup: React.FC<ProductLookupProps> = ({
  value,
  productResults,
  selectedProductIndex,
  dropdownPosition,
  isLoadingProducts = false,
  disabled = false,
  inputRef,
  selectedProductRowRef,
  justClosedDropdownRef,
  onChange,
  onFocus,
  onKeyDown,
  onProductPick,
  onSetActiveRow,
  onSetProductResults,
  onSetDropdownPosition,
  onSetSelectedIndex,
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    // Only show dropdown if there's a search term and we didn't just close it
    if (newValue.trim().length >= 2 && !justClosedDropdownRef.current) {
      onSetActiveRow(true);
    } else if (newValue.trim().length < 2) {
      // Clear dropdown if search term is too short
      onSetActiveRow(false);
      onSetProductResults([]);
      onSetDropdownPosition(null);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    onFocus();
    // Select all text when tabbing to the search box
    e.target.select();

    // Prevent reopening if we just closed the dropdown
    if (justClosedDropdownRef.current) {
      return;
    }

    // Only open dropdown if there's a search term
    if (value && value.trim().length >= 2) {
      onSetActiveRow(true);
      onSetSelectedIndex(0);
    }
  };

  return (
    <>
      <div className="relative">
        <div data-product-input ref={inputRef} className="flex items-center">
          <div className="flex-1">
            <Input
              placeholder="Type to search product (SKU, name, tags)"
              value={value}
              onChange={handleChange}
              onFocus={handleFocus}
              onKeyDown={onKeyDown}
              iconRight={
                isLoadingProducts && value.trim().length >= 2
                  ? Icon.Loading
                  : undefined
              }
              iconClassName={isLoadingProducts ? "animate-spin" : undefined}
              disabled={disabled}
              className="py-0.5"
            />
          </div>
        </div>
      </div>

      {/* Product dropdown with fixed positioning to overflow container */}
      {dropdownPosition && value.trim().length >= 2 && (
        <div
          data-product-dropdown
          className="fixed z-50 max-h-56 overflow-y-auto overflow-x-hidden bg-white border border-neutral-20 rounded shadow"
          style={{
            top: dropdownPosition.openUpward
              ? "auto"
              : `${dropdownPosition.top}px`,
            bottom: dropdownPosition.openUpward
              ? `${window.innerHeight - dropdownPosition.top + 1}px`
              : "auto",
            left: `${dropdownPosition.left}px`,
            minWidth: `${dropdownPosition.width}px`,
          }}
        >
          {isLoadingProducts ? (
            <div className="px-3 py-2 text-center text-neutral-60">
              Loading...
            </div>
          ) : productResults.length === 0 ? (
            <div className="px-3 py-2 text-center text-neutral-60">
              No products found
            </div>
          ) : (
            <div className="py-0">
              <div className="tom-table">
                <Table
                  className="w-auto border-separate"
                  style={{ borderSpacing: 0 }}
                >
                  <tbody>
                    {productResults.map((product, index) => (
                      <tr
                        key={`${product.id}-${product.sku}`}
                        ref={
                          index === selectedProductIndex
                            ? (selectedProductRowRef as React.RefObject<HTMLTableRowElement>)
                            : null
                        }
                        className={`hover:bg-neutral-10 cursor-pointer ${
                          index === selectedProductIndex
                            ? "border-2 border-blue"
                            : "border-2 border-transparent"
                        }`}
                        style={{
                          backgroundColor:
                            index === selectedProductIndex
                              ? "#e6f2ff"
                              : product.isFromPantry
                              ? "#ddf0f9"
                              : undefined,
                        }}
                        onClick={() => onProductPick(product)}
                      >
                        <Td className="text-left text-base min-w-10 whitespace-nowrap border-0 py-1 px-2">
                          {product.sku || ""}
                        </Td>
                        <Td className="text-left text-base min-w-10 whitespace-nowrap border-0 py-1 px-2">
                          {product.productCategory || ""}
                        </Td>
                        <Td className="text-left text-base min-w-[300px] border-0 p-1">
                          <div className="flex items-center">
                            {product.productImageUrl && (
                              <img
                                src={product.productImageUrl}
                                alt=""
                                className="w-8 h-8 object-cover"
                              />
                            )}
                            <span className="pl-2">{product.name || ""}</span>
                            {product.isSupplierSellProduct && (
                              <span className="ml-2" title="Supplier product">
                                <Icon.CheckedCircle
                                  size="14"
                                  className="fill-blue-dark"
                                />
                              </span>
                            )}
                            {product.isFavourite &&
                              product.isFromProductAgreement && (
                                <span
                                  className="ml-2"
                                  title="Pantry list favourite product"
                                >
                                  <Icon.StarFilled
                                    size="14"
                                    className="fill-blue-dark"
                                  />
                                </span>
                              )}
                            {product.isFromProductAgreement === false && (
                              <span
                                className="ml-2"
                                title="Warning! This product is not in the sales product agreement list."
                              >
                                <Icon.WarningCircle
                                  size="14"
                                  className="fill-yellow"
                                />
                              </span>
                            )}
                          </div>
                        </Td>
                        <Td className="text-left text-base min-w-10 whitespace-nowrap border-0 py-1 px-2">
                          {product.unit || ""}
                        </Td>
                        <Td className="text-right text-base min-w-10 whitespace-nowrap border-0 py-1 px-2">
                          {formatCurrency(product.price || 0)}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ProductLookup;
